import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { ArrowRight, CheckCircle, ChevronDown, Cpu, Database, Download, GitBranch, Scale, Settings, ShieldCheck, ShieldAlert, TrendingUp, Zap, GraduationCap } from 'lucide-react';
import { trackPlausibleEvent } from '../utils/tracking';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { StatCard } from '../components/ui/StatCard';
import { WhatNextSection } from '../components/WhatNextSection';

const AccordionItem: React.FC<{ title: string; children: React.ReactNode; icon: React.ReactNode; defaultOpen?: boolean; }> = ({ title, children, icon, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const contentId = `accordion-content-${title.replace(/\s+/g, '-')}`;
    return (
        <div className="border-b border-white/10 dark:border-white/5">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex justify-between items-center py-5 text-left group"
                aria-expanded={isOpen}
                aria-controls={contentId}
            >
                <span className="font-semibold text-lg flex items-center gap-3 text-text-primary-light dark:text-text-primary-dark group-hover:text-primary-light dark:group-hover:text-primary-dark transition-colors">
                    <span className="text-primary-light dark:text-primary-dark">{icon}</span>
                    {title}
                </span>
                <div className={`w-8 h-8 rounded-lg bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center transition-all duration-300 ${isOpen ? 'rotate-180 bg-primary-light/20 dark:bg-primary-dark/20' : ''}`}>
                    <ChevronDown className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                </div>
            </button>
            <div
                id={contentId}
                className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
            >
                <div className="pb-5 pl-10 text-text-secondary-light dark:text-text-secondary-dark">{children}</div>
            </div>
        </div>
    );
};

const PromptCard: React.FC<{ title: string; content: string; }> = ({ title, content }) => {
    const [copied, setCopied] = useState(false);
    const copyToClipboard = () => {
        navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <GlassCard variant="subtle" className="border-l-4 border-primary-light dark:border-primary-dark font-mono text-sm relative mt-4">
            <button onClick={copyToClipboard} className="absolute top-3 right-3 px-3 py-1 text-xs font-semibold bg-primary-light/80 dark:bg-primary-dark/80 text-white rounded-lg hover:bg-primary-light dark:hover:bg-primary-dark transition-colors">
                {copied ? 'Skopiowano!' : 'Kopiuj'}
            </button>
            <strong className="block mb-2 font-sans font-bold text-text-primary-light dark:text-text-primary-dark">{title}</strong>
            <pre className="whitespace-pre-wrap font-mono text-text-secondary-light dark:text-text-secondary-dark">{content}</pre>
        </GlassCard>
    );
};

export const SzkolenieAiKancelariePage: React.FC = () => {
    usePageMetadata({
        title: "Szkolenie AI dla Kancelarii Prawnych | Edycja 2025",
        description: "Naucz się automatyzować pracę, analizować dokumenty i oszczędzać do 70% czasu. Kompletne szkolenie z wdrożenia AI w kancelarii prawnej.",
    });

    const stats = [
        { value: '70%', label: 'Redukcja czasu' },
        { value: '340%', label: 'Średnie oszczędności' },
        { value: '45%', label: 'Wzrost liczby spraw' },
        { value: '2h', label: 'Czas wdrożenia' }
    ];

    const downloadLinks = [
        { href: '/data/prompty-prawnicze.json', label: 'Prompty', file: 'prompty-prawnicze.json' },
        { href: '/data/polityka-ai-kancelaria.md', label: 'Polityka AI', file: 'polityka-ai-kancelaria.md' },
        { href: '/data/checklist-wdrozenia.md', label: 'Checklist', file: 'checklist-wdrozenia.md' },
        { href: '/data/automatyzacje-workflow.json', label: 'Workflow', file: 'automatyzacje-workflow.json' },
        { href: '/lead-magnets/15-pomyslow-ai.md', label: '15 Pomysłów .MD', file: '15-pomyslow-ai.md', variant: 'secondary' }
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll>
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                                <GraduationCap className="w-10 h-10 text-white" />
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                                AI dla Kancelarii Prawnych
                            </h1>
                            <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto">
                                Kompletna platforma szkoleniowa: od podstaw do eksperckiego wdrożenia w 90 dni. Edycja 2025.
                            </p>
                        </div>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
                            {stats.map((stat, index) => (
                                <StatCard key={index} value={stat.value} label={stat.label} variant={index === 0 ? 'highlight' : 'default'} />
                            ))}
                        </div>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <div className="text-center">
                            <Link
                                to="/kontakt"
                                className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-2xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center gap-2 animate-glow"
                            >
                                <Zap className="w-5 h-5" />
                                Zapisz się na warsztat
                            </Link>
                        </div>
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            {/* Agenda Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 max-w-4xl relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Agenda Warsztatu: Od Zera do Działającego AI
                        </h2>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <GlassCard variant="medium">
                            <AccordionItem title="Moduł 1: Architektura AI i Diagnoza Rynku" icon={<Cpu className="w-5 h-5" />} defaultOpen={true}>
                                <p>Zrozumienie fundamentów: porównanie kluczowych modeli AI (GPT-4.1 vs Claude 3.5 vs Gemini 2.5), analiza ich mocnych i słabych stron w kontekście prawnym. Diagnoza rynku na 2025 rok - dlaczego AI to już konieczność, a nie wybór.</p>
                            </AccordionItem>
                            <AccordionItem title="Moduł 2: Konfiguracja i System Message" icon={<Settings className="w-5 h-5" />}>
                                <p className="mb-4">Praktyczne warsztaty z konfiguracji ChatGPT i Claude. Stworzymy od podstaw "System Message" - DNA Twojego asystenta prawnego, który zapewni precyzyjne i bezpieczne odpowiedzi.</p>
                                <PromptCard title="Przykładowy System Message" content={`Jesteś ekspertem prawnym i asystentem kancelarii [NAZWA]. ROLA: Senior counsel z 15-letnim doświadczeniem w [SPECJALIZACJA]. OGRANICZENIA: NIE wymyślaj przepisów ani orzeczeń. Jeśli nie masz pewności - napisz "WYMAGA WERYFIKACJI". Zawsze wskaż źródło.`} />
                            </AccordionItem>
                            <AccordionItem title="Moduł 3: Prompt Engineering i Anty-Halucynacje" icon={<GitBranch className="w-5 h-5" />}>
                                <p>Nauka tworzenia skutecznych promptów dla zadań prawniczych: od analizy umów po pisma procesowe. Wdrożenie systemu "7P" (Przepisy, Precedensy, Podstawa, etc.) w celu minimalizacji ryzyka "halucynacji" AI.</p>
                            </AccordionItem>
                            <AccordionItem title="Moduł 4: Automatyzacja i Case Studies" icon={<Zap className="w-5 h-5" />}>
                                <p>Przegląd gotowych automatyzacji: masowa windykacja, generator dokumentów, inteligentny research. Analiza case study wdrożenia AI w restrukturyzacji firmy transportowej - krok po kroku.</p>
                            </AccordionItem>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            {/* ROI Calculator CTA */}
            <section className="py-16 md:py-20 relative overflow-hidden">
                <AnimatedBlob variant="purple" />
                <ParticlesBackground variant="minimal" />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="strong" className="max-w-4xl mx-auto text-center">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                                Oblicz Realny Zwrot z Inwestycji
                            </h2>
                            <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-8">
                                Zamiast teoretycznych szacunków, użyj naszego zaawansowanego kalkulatora, aby zobaczyć konkretne liczby. Wprowadź dane swojej kancelarii i odkryj, ile możesz zaoszczędzić i zarobić dzięki wdrożeniu AI.
                            </p>
                            <Link
                                to="/edukacja/kalkulator-roi-ai"
                                className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group"
                            >
                                Policz swoje oszczędności
                                <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            {/* 90-Day Plan */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="lines" animated={false} />
                <div className="container mx-auto px-4 max-w-4xl relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Plan Wdrożenia w 90 Dni
                        </h2>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <GlassCard variant="medium">
                            <AccordionItem title="Faza 1: Fundamenty (Dni 1-7)" icon={<Database className="w-5 h-5" />} defaultOpen={true}>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Powołanie zespołu wdrożeniowego AI i wyznaczenie lidera.</li>
                                    <li>Analiza potrzeb kancelarii i identyfikacja 5 najbardziej czasochłonnych procesów.</li>
                                    <li>Wybór i bezpieczna konfiguracja narzędzi AI (ChatGPT Teams, Claude Pro).</li>
                                    <li>Szkolenie podstawowe dla całego zespołu (2h wprowadzenia + 3h warsztatów).</li>
                                </ul>
                            </AccordionItem>
                            <AccordionItem title="Faza 2: Ekspansja (Dni 8-30)" icon={<TrendingUp className="w-5 h-5" />}>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Wdrożenie pilotażowe w jednym dziale (np. windykacja).</li>
                                    <li>Stworzenie wewnętrznej biblioteki 50+ promptów dla najczęstszych zadań.</li>
                                    <li>Implementacja pierwszych 3 automatyzacji workflow (np. wezwania do zapłaty).</li>
                                </ul>
                            </AccordionItem>
                            <AccordionItem title="Faza 3: Optymizacja (Dni 31-90)" icon={<Scale className="w-5 h-5" />}>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Pełne wdrożenie AI we wszystkich działach kancelarii.</li>
                                    <li>Wyliczenie, co i ile realnie oszczędza.</li>
                                    <li>Wdrożenie zaawansowanych narzędzi: Custom GPTs, lokalne LLM dla danych wrażliwych.</li>
                                </ul>
                            </AccordionItem>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            {/* Security Section */}
            <section className="py-16 md:py-20 relative">
                <div className="container mx-auto px-4 max-w-4xl relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Bezpieczeństwo i RODO
                        </h2>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 gap-8">
                        <RevealOnScroll delay={100}>
                            <GlassCard variant="subtle" className="bg-red-500/10 border border-red-500/30">
                                <h3 className="font-bold text-lg text-red-600 dark:text-red-400 mb-4 flex items-center gap-2">
                                    <ShieldAlert className="w-5 h-5" /> Czego NIGDY nie robić
                                </h3>
                                <ul className="space-y-2 text-sm text-red-900 dark:text-red-200">
                                    <li className="flex items-start gap-2"><span className="text-red-500">•</span> Nie wprowadzaj prawdziwych danych osobowych klientów.</li>
                                    <li className="flex items-start gap-2"><span className="text-red-500">•</span> Nie kopiuj całych dokumentów bez anonimizacji.</li>
                                    <li className="flex items-start gap-2"><span className="text-red-500">•</span> Nie używaj numerów PESEL, NIP, REGON.</li>
                                    <li className="flex items-start gap-2"><span className="text-red-500">•</span> Nie udostępniaj loginów do AI osobom trzecim.</li>
                                </ul>
                            </GlassCard>
                        </RevealOnScroll>

                        <RevealOnScroll delay={200}>
                            <GlassCard variant="subtle" className="bg-green-500/10 border border-green-500/30">
                                <h3 className="font-bold text-lg text-green-600 dark:text-green-400 mb-4 flex items-center gap-2">
                                    <ShieldCheck className="w-5 h-5" /> Dobre praktyki
                                </h3>
                                <ul className="space-y-2 text-sm text-green-900 dark:text-green-200">
                                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Zawsze anonimizuj dane przed wprowadzeniem.</li>
                                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Wyłącz historię czatu w ustawieniach narzędzia.</li>
                                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Weryfikuj każdy output wygenerowany przez AI.</li>
                                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> Dokumentuj wykorzystanie AI w aktach sprawy.</li>
                                </ul>
                            </GlassCard>
                        </RevealOnScroll>
                    </div>
                </div>
            </section>

            {/* Downloads Section */}
            <section className="py-16 md:py-20 relative overflow-hidden">
                <AnimatedBlob variant="blue" />
                <ParticlesBackground variant="minimal" />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="strong" className="max-w-4xl mx-auto text-center">
                            <Download className="w-12 h-12 mx-auto mb-4 text-primary-light dark:text-primary-dark" />
                            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-text-primary-light dark:text-text-primary-dark">
                                Pobierz Kompletny Pakiet Materiałów
                            </h2>
                            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">
                                Wszystko, czego potrzebujesz, aby zacząć – za darmo.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                {downloadLinks.map((link, index) => (
                                    <a
                                        key={index}
                                        href={link.href}
                                        download
                                        onClick={() => trackPlausibleEvent('pdf_download', { file: link.file })}
                                        className={`px-5 py-2 font-semibold text-white rounded-full hover:opacity-90 transition-all inline-flex items-center gap-2 ${
                                            link.variant === 'secondary'
                                                ? 'bg-secondary-light dark:bg-secondary-dark'
                                                : 'bg-primary-light dark:bg-primary-dark'
                                        }`}
                                    >
                                        <Download size={16} /> {link.label}
                                    </a>
                                ))}
                            </div>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            <WhatNextSection />
        </>
    );
};
